// Parse structured data from AI agent responses
import type { IssueRecorderOutput } from '@/types/ai'

/**
 * Extract JSON data from agent response text.
 * Handles: fenced code blocks, raw JSON objects, key-value text.
 */
export function extractJsonFromText(text: string): Record<string, unknown> | null {
  // Try fenced JSON block: ```json { ... } ```
  const fencedMatch = text.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/)
  if (fencedMatch) {
    try { return JSON.parse(fencedMatch[1]) } catch { /* continue */ }
  }

  // Try raw JSON object in text
  const jsonMatch = text.match(/\{[\s\S]*"(?:extractedData|problemTitle|title|d365Version)"[\s\S]*\}/)
  if (jsonMatch) {
    try { return JSON.parse(jsonMatch[0]) } catch { /* continue */ }
  }

  return null
}

/**
 * Parse issue recorder agent response into structured output.
 */
export function parseIssueRecorderResponse(content: string): Partial<IssueRecorderOutput> {
  const json = extractJsonFromText(content)

  if (json) {
    const quickReplies = (json.suggestedQuickReplies as string[]) ?? []

    // FORCE multi-select for any question with 3+ options (frontend override)
    const allowMultiSelect = quickReplies.length >= 3 ? true : ((json.allowMultiSelect as boolean) ?? false)

    const result = {
      responseMessage: content,
      extractedData: (json.extractedData as Record<string, unknown>) ?? json,
      nextAction: (json.nextAction as 'continue' | 'validate' | 'complete') ?? 'continue',
      suggestedQuickReplies: quickReplies,
      allowMultiSelect,
      completeness: (json.completeness as number) ?? 0,
    }
    console.log('[parseIssueRecorderResponse] Parsed from JSON:', {
      hasMultiSelect: 'allowMultiSelect' in json,
      originalAllowMultiSelect: json.allowMultiSelect,
      forcedAllowMultiSelect: allowMultiSelect,
      quickRepliesCount: quickReplies.length,
      quickReplies,
    })
    return result
  }

  // Try to extract key-value pairs from structured text
  const extracted: Record<string, string> = {}
  const kvPatterns = [
    { key: 'problemTitle', pattern: /(?:title|problem|issue)[:\s]+["']?([^"'\n]+)/i },
    { key: 'd365Version', pattern: /(?:version|d365)[:\s]+["']?(10\.0\.\d+)/i },
    { key: 'affectedModules', pattern: /(?:module|area)[:\s]+["']?([^"'\n]+)/i },
    { key: 'priority', pattern: /(?:priority|severity)[:\s]+["']?(critical|high|medium|low)/i },
  ]

  for (const { key, pattern } of kvPatterns) {
    const match = content.match(pattern)
    if (match) extracted[key] = match[1].trim()
  }

  // Check for completeness indicator
  const completenessMatch = content.match(/(\d+)%\s*(?:complete|done|progress)/i)
  const completeness = completenessMatch ? parseInt(completenessMatch[1], 10) : 0

  // Extract quick replies
  const quickReplies: string[] = []
  const qrMatch = content.match(/(?:options?|choose|select)[:\s]*\n((?:\s*[-•]\s*.+\n?)+)/i)
  if (qrMatch) {
    const lines = qrMatch[1].split('\n')
    for (const line of lines) {
      const clean = line.replace(/^\s*[-•]\s*/, '').trim()
      if (clean) quickReplies.push(clean)
    }
  }

  return {
    responseMessage: content,
    extractedData: Object.keys(extracted).length > 0 ? extracted : {},
    nextAction: completeness >= 80 ? 'validate' : 'continue',
    suggestedQuickReplies: quickReplies,
    completeness,
  }
}
